import { useContext, useEffect } from "react";
import { SocketContext } from "src/contexts/SocketContext";
import useAppStore from "src/stores/appStore";
import { assistantIdSelector } from "src/stores/selectors/appSelectors";
import { draftSelector } from "src/stores/selectors/tripletsSelectors";
import { queryClient } from "src/lib/react-query/queryClient";
import { FETCH_QUERY_KEYS } from "src/queries/consts";
import { BUILD_STATUSES, BRANCH_NAMES } from "@consts";

const EVENTS = {
  CONNECT: "connect",
  REGISTER: "build-status:register",
  UPDATE: "build-status:update",
  REQUEST_STATUS: "build-status:request-status",
  ERROR: "error"
};

const PREVIEW_STATES = {
  DRAFT: "previewDraft",
  LIVE: "previewLive"
};

const TERMINAL_STATUSES = [BUILD_STATUSES.LIVE, BUILD_STATUSES.CHANGED, ""];
const FAILED_STATUS = BUILD_STATUSES.FAILED;

const ENVIRONMENT_TO_STATE_KEY = {
  draft: PREVIEW_STATES.DRAFT,
  preview: PREVIEW_STATES.LIVE
};

const REGISTERED_ASSISTANT_KEY = "_buildStatusRegisteredAssistantId";

const useBuildStatusSocket = (enabled, { isLivePreview = false, branchName: branchNameParam, releaseName } = {}) => {
  const assistantId = useAppStore(assistantIdSelector);
  const socket = useContext(SocketContext);
  const draft = useAppStore(draftSelector);

  useEffect(() => {
    if (!enabled || !socket || !assistantId) return;

    const branchName = branchNameParam || (isLivePreview ? BRANCH_NAMES.MASTER : draft?.branchName);
    const stateKey = isLivePreview ? PREVIEW_STATES.LIVE : PREVIEW_STATES.DRAFT;

    const requestStatus = () => {
      socket.emit(EVENTS.REQUEST_STATUS, { assistantId, branchName, releaseName });
      useAppStore.setState(state => ({
        [stateKey]: { ...state[stateKey], isStatusPollingInProgress: true, hasFailedPolling: false }
      }));
    };

    const emitRegistration = () => {
      socket.emit(EVENTS.REGISTER, { assistantId });
    };

    const handleConnect = () => {
      delete socket[REGISTERED_ASSISTANT_KEY];
      emitRegistration();
    };

    const handleRegisterSuccess = (data) => {
      if (data?.assistantId !== assistantId) return;
      socket[REGISTERED_ASSISTANT_KEY] = assistantId;
      if (!useAppStore.getState()[stateKey]?.buildResponse) {
        requestStatus();
      }
    };

    const handleStatusUpdate = (data) => {
      const { status, url, environment } = data;
      const updateStateKey = ENVIRONMENT_TO_STATE_KEY[environment];
      if (!updateStateKey) return;

      const buildResponse = { status: status || "", url };
      const isBuildSuccessful = TERMINAL_STATUSES.includes(status);
      const isBuildComplete = isBuildSuccessful || status === FAILED_STATUS;

      if (isBuildSuccessful) {
        queryClient.invalidateQueries({ queryKey: [FETCH_QUERY_KEYS.PREVIEW_BRANCH_DEPLOYER] });
      }

      useAppStore.setState(state => ({
        [updateStateKey]: {
          ...state[updateStateKey],
          buildResponse,
          isStatusPollingInProgress: !isBuildComplete,
          hasFailedPolling: status === FAILED_STATUS,
          lastBuildAbort: null
        }
      }));
    };

    const handleSocketError = (error) => {
      console.error("Build status socket error:", error);
    };

    socket.on(EVENTS.CONNECT, handleConnect);
    socket.on(EVENTS.REGISTER, handleRegisterSuccess);
    socket.on(EVENTS.UPDATE, handleStatusUpdate);
    socket.on(EVENTS.ERROR, handleSocketError);

    if (socket.connected) {
      if (socket[REGISTERED_ASSISTANT_KEY] === assistantId) {
        if (!useAppStore.getState()[stateKey]?.buildResponse) {
          requestStatus();
        }
      } else {
        emitRegistration();
      }
    }

    return () => {
      socket.off(EVENTS.CONNECT, handleConnect);
      socket.off(EVENTS.REGISTER, handleRegisterSuccess);
      socket.off(EVENTS.UPDATE, handleStatusUpdate);
      socket.off(EVENTS.ERROR, handleSocketError);
    };
  }, [enabled, socket, assistantId, draft?.branchName, isLivePreview, branchNameParam, releaseName]);
};

export default useBuildStatusSocket;
